import { Dialog, Heading } from '@madpaws/design-system';
import { Formik } from 'formik';
import getConfig from 'next/config';
import React from 'react';

import { ImageWithFallback } from '~/common/components/ImageWithFallback/ImageWithFallback';
import { MobileNumberForm } from '~/components/AuthenticationProvider/MobileNumberDialog/MobileNumberForm';
import { mobileNumberFormSchema } from '~/components/AuthenticationProvider/MobileNumberDialog/utils';
import { DOM_CONTAINER_ID } from '~/components/constants';
import { setLocalStorage, TEMP_USER_MOBILE_LOCAL_STORAGE_KEY } from '~/utils/localStorage';

import styles from './MobileNumber.module.css';
import { useAuthenticationContext } from '../useAuthenticationContext';

import type { ReactElement } from 'react';

const { publicRuntimeConfig } = getConfig();

type Props = {
  handleValidatePhoneSuccess?: (userID: number) => void;
};

export type MobileNumberFormValues = {
  mobileNumber: string;
};

const formInitialValues: MobileNumberFormValues = {
  mobileNumber: '',
};

export const MobileNumberDialog = ({ handleValidatePhoneSuccess }: Props): ReactElement => {
  const {
    isMobileNumberDialogOpen,
    setIsMobileNumberDialogOpen,
    user,
  } = useAuthenticationContext();

  const handleDialogClose = (): void => {
    setIsMobileNumberDialogOpen(false);
  };

  const onSubmitForm = (values: MobileNumberFormValues): void => {
    setLocalStorage(TEMP_USER_MOBILE_LOCAL_STORAGE_KEY, values.mobileNumber, false);
    handleValidatePhoneSuccess?.(user?.id as number);
    handleDialogClose();
  };

  return (
    <Dialog
      domContainerId={DOM_CONTAINER_ID}
      heading="One last thing!"
      isOpen={isMobileNumberDialogOpen}
      onRequestClose={handleDialogClose}
    >
      <div className={styles.container}>
        <ImageWithFallback
          alt="Picture of a pet"
          fallback={`${publicRuntimeConfig.staticPath}/images/auth/dogMobile.png`}
          loading="lazy"
          webpSrc={`${publicRuntimeConfig.staticPath}/images/auth/dogMobile.webp`}
          width="100%"
        />
        <Heading color="brandColor" level={2} size="small2x">
          Add your phone number
        </Heading>
        <p>Details are never shared without consent.</p>
      </div>

      <Formik
        initialValues={formInitialValues}
        onSubmit={onSubmitForm}
        validationSchema={mobileNumberFormSchema}
      >
        <MobileNumberForm apiError={''} isAPILoading={false} />
      </Formik>
    </Dialog>
  );
};
